import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../components/layout/DashboardLayout';
import DataTable from '../components/common/DataTable';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import usePageTitle from '../hooks/usePageTitle';
import useSmartRefresh from '../hooks/useSmartRefresh';

/**
 * Invoices Page - Professional invoice management interface using DataTable
 */
const InvoicesPage = () => {
  // Set page title
  usePageTitle('Invoices');

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Mock data fetching function
  const fetchInvoicesData = async () => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Invoices data refreshed');
  };

  const { isRefreshing, refreshData } = useSmartRefresh(
    fetchInvoicesData,
    'Invoices refreshed successfully'
  );

  // Mock invoices data based on subscription plans
  const invoices = [
    {
      id: 'INV-2025-001',
      number: '#001',
      amount: 49.99,
      tax: 7.50,
      total: 57.49,
      status: 'paid',
      date: '2025-01-15',
      dueDate: '2025-02-15',
      items: 1,
      description: 'Professional Plan Subscription - January 2025'
    },
    {
      id: 'INV-2024-012',
      number: '#012',
      amount: 49.99,
      tax: 7.50,
      total: 57.49,
      status: 'paid',
      date: '2024-12-15',
      dueDate: '2025-01-15',
      items: 1,
      description: 'Professional Plan Subscription - December 2024'
    },
    {
      id: 'INV-2024-011',
      number: '#011',
      amount: 49.99,
      tax: 7.50,
      total: 57.49,
      status: 'paid',
      date: '2024-11-15',
      dueDate: '2024-12-15',
      items: 1,
      description: 'Professional Plan Subscription - November 2024'
    },
    {
      id: 'INV-2024-010',
      number: '#010',
      amount: 9.99,
      tax: 1.50,
      total: 11.49,
      status: 'paid',
      date: '2024-10-15',
      dueDate: '2024-11-15',
      items: 1,
      description: 'Basic Plan Subscription - October 2024'
    },
    {
      id: 'INV-2025-002',
      number: '#002',
      amount: 49.99,
      tax: 7.50,
      total: 57.49,
      status: 'pending',
      date: '2025-02-15',
      dueDate: '2025-03-15',
      items: 1,
      description: 'Professional Plan Renewal - February 2025'
    }
  ];

  // Define table columns for invoices with equal distribution
  const columns = [
    {
      id: 'number',
      label: 'Invoice #',
      sortable: true,
      width: '15%',
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{value}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{item.id}</div>
        </div>
      )
    },
    {
      id: 'description',
      label: 'Description',
      sortable: true,
      width: '30%',
      render: (value) => (
        <div className="min-w-0">
          <p className="text-gray-900 dark:text-gray-100 text-sm leading-5 break-words" title={value}>
            {value}
          </p>
        </div>
      )
    },
    {
      id: 'items',
      label: 'Items',
      sortable: true,
      align: 'center',
      width: '10%',
      render: (value) => (
        <span className="text-gray-900 dark:text-gray-100">{value}</span>
      )
    },
    {
      id: 'total',
      label: 'Total',
      sortable: true,
      align: 'center',
      width: '12%',
      render: (value) => (
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          ${value.toFixed(2)}
        </span>
      )
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      align: 'center',
      width: '15%',
      render: (value) => {
        const statusConfig = {
          paid: { 
            bg: 'bg-success-100 dark:bg-success-900/30', 
            text: 'text-success-700 dark:text-success-400',
            icon: (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )
          },
          pending: { 
            bg: 'bg-warning-100 dark:bg-warning-900/30', 
            text: 'text-warning-700 dark:text-warning-400',
            icon: (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )
          },
          overdue: { 
            bg: 'bg-error-100 dark:bg-error-900/30', 
            text: 'text-error-700 dark:text-error-400',
            icon: (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            )
          }
        };

        const config = statusConfig[value] || statusConfig.pending;

        return (
          <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            {config.icon}
            <span className="capitalize">{value}</span>
          </span>
        );
      }
    },
    {
      id: 'date',
      label: 'Date',
      sortable: true,
      width: '18%',
      render: (value, item) => (
        <div>
          <div className="text-gray-900 dark:text-gray-100">{value}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Due: {item.dueDate}</div>
        </div>
      )
    }
  ];

  // Define table actions for invoices
  const actions = [
    {
      type: 'view',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      tooltip: 'View Invoice Details',
      variant: 'primary'
    },
    {
      type: 'download',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      tooltip: 'Download Invoice'
    }
  ];

  // Define filters for invoices
  const filters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'paid', label: 'Paid' },
        { value: 'pending', label: 'Pending' },
        { value: 'overdue', label: 'Overdue' }
      ]
    }
  ];

  // Calculate totals
  const totalAmount = invoices.reduce((sum, invoice) => sum + invoice.total, 0);
  const paidAmount = invoices.filter(i => i.status === 'paid').reduce((sum, invoice) => sum + invoice.total, 0);
  const pendingAmount = invoices.filter(i => i.status !== 'paid').reduce((sum, invoice) => sum + invoice.total, 0);

  // Action handlers
  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const handleDownload = (invoice) => {
    handleDownloadReceipt(invoice);
  };

  const handleDownloadReceipt = (invoice) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('DeviceLink Global Pvt. Ltd.', 20, 20);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Professional IMEI Services', 20, 30);
    doc.text('Invoice Receipt', 20, 45);
    
    // Invoice Details
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Details:', 20, 65);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice Number: ${invoice.number}`, 20, 75);
    doc.text(`Date: ${invoice.date}`, 20, 85);
    doc.text(`Due Date: ${invoice.dueDate}`, 20, 95);
    doc.text(`Status: ${invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}`, 20, 105);
    
    // Service Details
    doc.setFont('helvetica', 'bold');
    doc.text('Service Details:', 20, 125);
    doc.setFont('helvetica', 'normal');
    doc.text(`Description: ${invoice.description}`, 20, 135);
    doc.text(`Amount: $${invoice.amount.toFixed(2)}`, 20, 145);
    doc.text(`Tax: $${invoice.tax.toFixed(2)}`, 20, 155);
    doc.text(`Total: $${invoice.total.toFixed(2)}`, 20, 165);
    
    // Footer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for choosing DeviceLink Global for your device verification needs.', 20, 180);
    doc.text('This document was generated electronically by DeviceLink Global Pvt. Ltd.', 20, 190);
    
    // Save
    doc.save(`invoice-${invoice.number}.pdf`);
  };

  return (
    <DashboardLayout pageName="Invoices">
      <div className="space-y-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Manage your billing and payment history with advanced filtering and search
          </p>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5-3H12M8.25 9h2.25" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Invoiced</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">${totalAmount.toFixed(2)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{invoices.length} invoices</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Paid Amount</p>
                <p className="text-2xl font-bold text-success-600 dark:text-success-400">${paidAmount.toFixed(2)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{invoices.filter(i => i.status === 'paid').length} paid</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-warning-600 dark:text-warning-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Outstanding</p>
                <p className="text-2xl font-bold text-warning-600 dark:text-warning-400">${pendingAmount.toFixed(2)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{invoices.filter(i => i.status !== 'paid').length} pending</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* DataTable Component */}
        <DataTable
          title="Invoice Management"
          columns={columns}
          data={invoices}
          actions={actions}
          onView={handleView}
          onDownload={handleDownload}
          onRefresh={refreshData}
          searchPlaceholder="Search invoices, descriptions..."
          filters={filters}
          rowsPerPageOptions={[5, 10, 20, 50]}
          defaultRowsPerPage={10}
          exportEnabled={true}
          refreshEnabled={true}
          isLoading={isRefreshing}
          className="mt-6"
        />
      </div>

      {/* Invoice View Modal */}
      <AnimatePresence>
        {selectedInvoice && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => setSelectedInvoice(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400">Invoice Details</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedInvoice.number}</p>
                  </div>
                  <button
                    onClick={() => setSelectedInvoice(null)}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Invoice Info */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Invoice Information</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Invoice Number</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedInvoice.number}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Date Issued</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedInvoice.date}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Due Date</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedInvoice.dueDate}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                          <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                            selectedInvoice.status === 'paid' ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400' :
                            selectedInvoice.status === 'pending' ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400' :
                            selectedInvoice.status === 'overdue' ? 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            <span className="capitalize">{selectedInvoice.status}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Amount Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Amount Details</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <p className="text-sm text-gray-600 dark:text-gray-400">Subtotal</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">${selectedInvoice.amount.toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between">
                          <p className="text-sm text-gray-600 dark:text-gray-400">Tax</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">${selectedInvoice.tax.toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
                          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Total</p>
                          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">${selectedInvoice.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Service Description */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Service Description</h3>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedInvoice.description}</p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
                  <button
                    onClick={() => handleDownloadReceipt(selectedInvoice)}
                    className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Download Receipt
                  </button>
                  <button
                    onClick={() => setSelectedInvoice(null)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
};

export default InvoicesPage;